import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Grid from "@mui/material/Grid";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getNavRenewalReport } from "../../services/Dashboard";
import { useQuery } from "react-query";
import { isValidDate } from "../../services/Utils";
import SearchIcon from "@mui/icons-material/Search";
import PublicIcon from "@mui/icons-material/Public";
import LaptopIcon from "@mui/icons-material/Laptop";
import getDataGridToolbarProps from "../common/CustomToolbar";
import { getColumnsData, TIMEZONE } from "./NavRenewalReportColumns";


// ─── Summary header metrics ───────────────────────────────────────────────────

const SummaryHeader = ({ rows }) => {
  const daysAdded = rows.reduce(
    (sum, r) => sum + (r.daysAddedOrRemoved > 0 ? r.daysAddedOrRemoved : 0),
    0,
  );
  const daysRemoved = rows.reduce(
    (sum, r) => sum + (r.daysAddedOrRemoved < 0 ? r.daysAddedOrRemoved : 0),
    0,
  );
  const netDays = daysAdded + daysRemoved;

  const metrics = [
    {
      label: "Days Added",
      value: `+${daysAdded}`,
      color: "success",
    },
    {
      label: "Days Removed",
      value: daysRemoved,
      color: "error",
    },
    {
      label: "Net Days",
      value: netDays >= 0 ? `+${netDays}` : `${netDays}`,
      color: netDays >= 0 ? "success" : "error",
    },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {metrics.map((m) => (
        <Grid item xs={12} sm={4} key={m.label}>
          <Card
            variant="outlined"
            sx={{
              textAlign: "center",
              py: 1.5,
              px: 2,
              bgcolor: "background.default",
            }}
          >
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--mui-palette-text-secondary, #666)",
                marginBottom: 4,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {m.label}
            </div>
            <div
              style={{
                fontSize: "1.5rem",
                fontWeight: 600,
                color:
                  m.color === "success"
                    ? "#2e7d32"
                    : m.color === "error"
                      ? "#c62828"
                      : "inherit",
              }}
            >
              {m.value}
            </div>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const NavRenewalReport = (props) => {
  const CustomToolbar = getDataGridToolbarProps("Navigation Renewal Report", true);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [pickerInputErrorText, setPickerInputErrorText] = useState(null);
  const [timezone, setTimezone] = useState(TIMEZONE.LOCAL);

  const [searchParams, setSearchParams] = useState(null);
  const [rows, setRows] = useState([]);
  const [fetchData, setFetchData] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const columns = getColumnsData(timezone);

  const { isLoading, isFetching } = useQuery(
    ["nav_renewal_report", searchParams],
    getNavRenewalReport,
    {
      enabled: fetchData && !!searchParams,
      onSuccess: ({ data }) => {
        setRows(data);
        setPage(0);
      },
      onSettled: () => {
        setFetchData(false);
      },
      refetchOnWindowFocus: false,
    },
  );

  const processSearch = () => {
    if (!isValidDate(new Date(fromDate)) || !isValidDate(new Date(toDate))) {
      setPickerInputErrorText("Please select valid From and To dates.");
      return;
    }
    if (fromDate > toDate) {
      setPickerInputErrorText("From Date must be before To Date.");
      return;
    }
    setPickerInputErrorText(null);
    const from = new Date(fromDate);
    from.setHours(0, 0, 0, 0);
    const to = new Date(toDate);
    to.setHours(23, 59, 59, 999);
    setSearchParams({
      fromDate: from.toISOString(),
      toDate: to.toISOString(),
    });
    setFetchData(true);
  };

  const totalPages = Math.ceil((rows?.length || 0) / pageSize) || 1;

  return (
    <div className="fragmentContainer">
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={12}>
            <Card sx={{ height: "96px" }}>
              <CardContent className="tile-cards">
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "16px",
                  }}
                >
                  <FormControl variant="standard" sx={{ minWidth: 150 }}>
                    <DatePicker
                      disableFuture
                      label="From Date"
                      views={["year", "month", "day"]}
                      value={fromDate}
                      timezone="UTC"
                      onChange={(val) => {
                        if (!isValidDate(new Date(val))) {
                          setPickerInputErrorText("Invalid From Date!");
                          return;
                        }
                        setPickerInputErrorText(null);
                        setFromDate(val);
                      }}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          {...params}
                          id="renewal-from-date"
                          variant="standard"
                          onKeyDown={(e) => e.preventDefault()}
                          error={
                            !!pickerInputErrorText &&
                            pickerInputErrorText.includes("From")
                          }
                        />
                      )}
                      maxDate={toDate || new Date()}
                      minDate={new Date("01-01-2023")}
                      required
                    />
                  </FormControl>

                  <FormControl variant="standard" sx={{ minWidth: 150 }}>
                    <DatePicker
                      disableFuture
                      label="To Date"
                      views={["year", "month", "day"]}
                      value={toDate}
                      timezone="UTC"
                      onChange={(val) => {
                        if (!isValidDate(new Date(val))) {
                          setPickerInputErrorText("Invalid To Date!");
                          return;
                        }
                        setPickerInputErrorText(null);
                        setToDate(val);
                      }}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          {...params}
                          id="renewal-to-date"
                          variant="standard"
                          onKeyDown={(e) => e.preventDefault()}
                          error={
                            !!pickerInputErrorText &&
                            pickerInputErrorText.includes("To")
                          }
                        />
                      )}
                      maxDate={new Date()}
                      minDate={fromDate || new Date("01-01-2023")}
                      required
                    />
                  </FormControl>

                  <Button
                    disabled={
                      !!pickerInputErrorText || !fromDate || !toDate
                    }
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={processSearch}
                  >
                    Search
                  </Button>

                  <Tooltip
                    title={
                      timezone === TIMEZONE.LOCAL
                        ? "Currently showing browser local time. Click to switch to UTC."
                        : "Currently showing UTC. Click to switch to local time."
                    }
                  >
                    <ToggleButtonGroup
                      value={timezone}
                      exclusive
                      size="small"
                      onChange={(_, val) => {
                        if (val) setTimezone(val);
                      }}
                      aria-label="Timezone"
                    >
                      <ToggleButton value={TIMEZONE.LOCAL} aria-label="Local time">
                        <LaptopIcon fontSize="small" sx={{ mr: 0.5 }} />
                        Local
                      </ToggleButton>
                      <ToggleButton value={TIMEZONE.UTC} aria-label="UTC">
                        <PublicIcon fontSize="small" sx={{ mr: 0.5 }} />
                        UTC
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Tooltip>

                  {pickerInputErrorText && (
                    <div
                      style={{
                        color: "#d32f2f",
                        fontSize: "0.75rem",
                        width: "100%",
                      }}
                    >
                      {pickerInputErrorText}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </Grid>
          {rows.length > 0 && (
            <Grid item xs={12} className="no-export">
              <SummaryHeader rows={rows} />
            </Grid>
          )}

          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ padding: 0, ":last-child": { padding: 0 } }}>
                <Box sx={{ height: "calc(100vh - 340px)", width: "100%" }}>
                  <DataGrid
                    loading={isLoading || isFetching}
                    getRowId={(row) => row?.subscriptionAccountingId}
                    rows={rows || []}
                    columns={columns}
                    checkboxSelection
                    disableSelectionOnClick
                    experimentalFeatures={{ newEditingApi: true }}
                    page={page}
                    onPageChange={(newPage) => setPage(newPage)}
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => {
                      setPageSize(newPageSize);
                      setPage(0);
                    }}
                    components={{ Toolbar: CustomToolbar }}
                    componentsProps={{
                      toolbar: {
                        totalPages,
                        onPageChange: (newPage) => setPage(newPage),
                      },
                    }}
                    initialState={{
                      sorting: {
                        sortModel: [{ field: "createdAt", sort: "desc" }],
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </LocalizationProvider>
    </div>
  );
};

export default NavRenewalReport;
